import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, styled } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined, normalizeFileName } from "../../../../../../common/utils/utils";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { EntregaObraClienteArquivoAuxModel } from "../../../../domain/models/entrega_obra_cliente_arquivo_aux";
import { EntregaObraClienteArquivoAuxForm, EntregaObraClienteArquivoAuxSchema } from "../../../schemas/entrega_obra_cliente_verificacao/entregaObraClienteArquivoAuxCadastro";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

/**
 * EntregaObraClienteArquivoAuxModal
 */

type Props = {
    open: boolean,
    saving: boolean,
    selectedEntregaObraArquivoAux: EntregaObraClienteArquivoAuxModel | null,
    idEntregaObraCliente: number,

    onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
    onDownload: (idEntregaObraClienteArquivoAux: number) => void,
    onSubmit: (form: EntregaObraClienteArquivoAuxForm) => void,
    onClose: () => void,

};

export const EntregaObraClienteArquivoAuxModal = ({
    open,
    saving,
    selectedEntregaObraArquivoAux,
    idEntregaObraCliente,

    onSubmit,
    onOpenChangeHistory,
    onDownload,
    onClose,
}: Props) => {
    const [entregaObraArquivo, setEntregaObraArquivo] = useState(selectedEntregaObraArquivoAux);

    const hookForm = useForm<EntregaObraClienteArquivoAuxForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        resolver: zodResolver(EntregaObraClienteArquivoAuxSchema),
    });

    useEffect(() => {
        if (open) {
            if (selectedEntregaObraArquivoAux) {

                hookForm.setValue('idEntregaObraCliente', idEntregaObraCliente);
                hookForm.setValue('idEntregaObraClienteArquivoAux', selectedEntregaObraArquivoAux.idEntregaObraClienteArquivoAux);
                hookForm.setValue('descricao', selectedEntregaObraArquivoAux.descricao);
                hookForm.setValue('limparArquivo', false);
            } else {
                hookForm.reset();
                hookForm.setValue('idEntregaObraCliente', idEntregaObraCliente);
            }
            setEntregaObraArquivo(selectedEntregaObraArquivoAux ?? null);
        }
    }, [
        idEntregaObraCliente,
        open,
        selectedEntregaObraArquivoAux,
        hookForm,
    ]);

    const handleClose = useCallback(() => {
        if (!saving) onClose();
    }, [
        saving,
        onClose,
    ]);

    const handleDownload = useCallback(() => {
        if (!saving && entregaObraArquivo)
            onDownload(entregaObraArquivo.idEntregaObraClienteArquivoAux);
    }, [
        saving,
        entregaObraArquivo,
        onDownload,
    ]);

    const handleDeleteFile = useCallback(() => {
        if (!saving) {
            if (window.confirm('Você deseja excluir este item?')) {
                hookForm.setValue('limparArquivo', true);
                setEntregaObraArquivo(null);
            }
        }
    }, [
        saving,
        hookForm,
    ]);

    const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
        if (!saving && e.target.files && e.target.files.length > 0) {
            hookForm.setValue('limparArquivo', false);
            onChange(e.target.files.item(0));
        }
    }, [
        saving,
        hookForm,
    ]);

    const handleSubmit = useCallback((form: EntregaObraClienteArquivoAuxForm) => {
        if (!saving) onSubmit(form);
    }, [
        saving,
        onSubmit,
    ]);

    const handleChangeHistory = useCallback(() => {
        if (selectedEntregaObraArquivoAux) {
            onOpenChangeHistory(selectedEntregaObraArquivoAux.idEntregaObraClienteArquivoAux, LogTipoTabela.ENTREGA_OBRA_CLIENTE_ARQUIVO_AUX);
        }
    }, [selectedEntregaObraArquivoAux, onOpenChangeHistory]);

    return (
        <Dialog
            open={open}
            slotProps={{
                paper: {
                    component: 'form',
                    onSubmit: hookForm.handleSubmit(handleSubmit),
                }
            }}
            sx={{
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "35rem",
                    },
                },
            }}>
            <DialogTitle>
                Cadastrar documento
            </DialogTitle>

            <DialogContent>
                <ModalLoading loading={saving}>
                    <FormContainer title={"Cadastro Arquivo"} arrangement="fullRow">
                        <>

                            <DefaultFormInput
                                name={'descricao'}
                                label={"Nome do Arquivo"}
                                readOnly={saving}
                                required={true}
                                reactHookForm={hookForm}
                            />

                            <div className="flex w-full">
                                <Controller
                                    name='arquivo'
                                    control={hookForm.control}
                                    render={({ field }) => (entregaObraArquivo && entregaObraArquivo.nome && entregaObraArquivo.nome.length > 0) ? (
                                        <div className="w-full flex flex-row justify-between items-center">
                                            <span
                                                className="cursor-pointer"
                                                onMouseDown={handleDownload}>
                                                {entregaObraArquivo.nome && entregaObraArquivo.nome.length > 40
                                                    ? `${entregaObraArquivo.nome.substring(0, 40)}...`
                                                    : entregaObraArquivo.nome}
                                            </span>
                                            <IconButton onClick={handleDeleteFile}>
                                                <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                                            </IconButton>
                                        </div>
                                    ) : (
                                        <PrimaryButton
                                            component="label"
                                            role={undefined}
                                            variant="contained"
                                            tabIndex={-1}
                                            disabled={saving}
                                            startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                                            {field.value ? normalizeFileName(field.value.name, 40) : "Escolher arquivo"}
                                            <VisuallyHiddenInput
                                                type="file"
                                                onChange={(e) => handleFileChange(e, field.onChange)}
                                                multiple={false} />
                                        </PrimaryButton>
                                    )} />
                            </div>
                        </>
                    </FormContainer>
                </ModalLoading>
            </DialogContent>

            <DialogActions>
                <div className="w-full h-full items-center justify-between flex p-4 gap-4">
                    <div>
                        {
                            isNotNullOrUndefined(selectedEntregaObraArquivoAux) &&
                            <PrimaryButton
                                buttonColor="yellow"
                                onClick={handleChangeHistory}>
                                <span className="text-xs">
                                    Historico de alterações
                                </span>
                            </PrimaryButton>
                        }
                    </div>

                    <div className="flex flex-row gap-2">
                        <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                            <h1
                                className="text-sm text-black dark:text-white"
                                onClick={handleClose}>
                                Cancelar
                            </h1>
                        </div>

                        <div className="w-[120px]">
                            {saving ? (
                                <PrimaryButton
                                    buttonColor="primary-green">
                                    Salvando...
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    type="submit"
                                    buttonColor="primary-green">
                                    Salvar
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </DialogActions>
        </Dialog>
    );
}